/**
 * Script Editor — a true, LIVING Final Draft page (Agent 02).
 *
 * Not a scene-card form: one continuous, typeset screenplay you write in
 * like Final Draft. Highlights:
 *
 *   - PURE FREE-TYPE. No "+ add scene" front door (a subtle hook remains
 *     for the Koko bridge / timeline). Type a slug to start a scene;
 *     Enter drops to the contextual next element type; the per-line chip
 *     (and Tab) sets a line's type explicitly. Empty start = a blank page
 *     you can type into, with an inline "drop a PDF" import.
 *   - @-REFERENCE SYSTEM (owned here, _GLOBAL.md G1). Type `@` → an
 *     autocomplete of existing entities + "create new"; refs render as
 *     clickable pills and feed the store's ref→scene index.
 *   - LIVING DOC (G6). A new character cue, a new slug location, or a new
 *     `@ref` auto-creates the entity in the store — whether the user or
 *     the Koko agent typed it.
 *   - CLICK → DRAWER (G6). Clicking any inline circle / location square /
 *     ref pill opens that entity's edit form in the shared drawer, in
 *     place — no navigation.
 *   - PER-CHARACTER ▶ play uses the character's VoiceProfile + `/tts`;
 *     per-DIALOGUE sound editing (delivery / tone / speed) inline (G8).
 *   - COMMENTS (Google-Docs style): select a line → comment; anchored
 *     margin threads; resolve / reopen / delete; persisted; Koko can
 *     comment too.
 *
 * Canonical content is `StudioScene.elements[]`; `body` stays mirrored
 * for back-compat. Comments + per-line sound persist in sidecar stores
 * (see scriptSidecars.ts) until the integrator folds them into the store.
 */

import { BasePanel } from "./BasePanel";
import { buildHelperParagraph, buildPixelBox, buildStudioButton } from "./dom";
import type {
  ScriptElement,
  StudioAsset,
  StudioCharacter,
  StudioLocation,
  StudioScene,
  StudioStore,
} from "../StudioStore";
import {
  bodyToElements,
  classifyType,
  cycleType,
  elementsToBody,
  extractRefTokens,
  findCharacterByName,
  makeElementId,
  nextTypeOnEnter,
  parseSlug,
  placeholderFor,
  resolveSceneElements,
  typeLabel,
  type ElementType,
} from "./scriptModel";
import {
  analyzeScript,
  clampText,
  countParsedScenes,
  extractPdfText,
  formatBytes,
  ingestParsedIntoStore,
  looksLikePdf,
  MAX_FILE_BYTES,
  resolveWorkerBaseUrl,
  toUserMessage,
} from "./scriptIngest";
import {
  DEFAULT_LINE_SOUND,
  LineSoundStore,
  ScriptCommentStore,
  type Delivery,
  type LineSound,
  type ScriptComment,
} from "./scriptSidecars";
import { LineVoicePlayer, resolveVoice } from "./scriptVoice";
import { buildPixelIdenticonImage } from "../shared/pixelIdenticon";
import { openEntityDrawer, closeEntityDrawer, openImageEditor } from "../shared";
// Cross-panel reuse (G6): the in-script click→drawer renders the SAME
// edit forms the entity panels use, not bespoke stubs.
import { CharacterDetailForm } from "./CharacterCreatorPanel";
import { buildAssetEditForm } from "./AssetsPanel";
import "../styles/script-editor.css";

interface ScriptEditorPanelOptions {
  store: StudioStore;
  /** Worker base URL — used for the `/tts` route on per-line playback. */
  workerBaseUrl: string;
}

type EntityKind = "character" | "location" | "asset" | "look";

interface EntityIndexEntry {
  type: EntityKind;
  id: string;
  name: string;
}

interface RefMenuState {
  textEl: HTMLElement;
  scene: StudioScene;
  index: number;
  /** Offset of the `@` in the line text. */
  tokenStart: number;
  query: string;
  items: RefMenuItem[];
  selected: number;
}

interface RefMenuItem {
  label: string;
  hint: string;
  /** Existing entity, or null for a "create new" action. */
  entry: EntityIndexEntry | null;
  createKind?: EntityKind;
  name: string;
}

export class ScriptEditorPanel extends BasePanel {
  private readonly store: StudioStore;
  private readonly commentStore = new ScriptCommentStore();
  private readonly lineSoundStore = new LineSoundStore();
  private readonly voicePlayer: LineVoicePlayer;

  private rootEl: HTMLDivElement | null = null;
  private pageEl: HTMLDivElement | null = null;
  private marginEl: HTMLDivElement | null = null;
  private refMenuEl: HTMLDivElement | null = null;

  private unsubscribers: (() => void)[] = [];
  private refMenu: RefMenuState | null = null;
  private focusedElementId: string | null = null;
  private showComments = false;
  /** Element ids whose type the user pinned via the chip / Tab. */
  private readonly pinnedTypes = new Set<string>();
  /** After a structural change, focus this (sceneId,index) on next render. */
  private pendingFocus: { sceneId: string; index: number } | null = null;

  constructor(options: ScriptEditorPanelOptions) {
    super();
    this.store = options.store;
    this.voicePlayer = new LineVoicePlayer(options.workerBaseUrl);
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "fd-root";
    this.rootEl = root;

    const box = buildPixelBox();

    // Top toolbar — comments toggle + import hint (free-type, no add-scene).
    const toolbar = document.createElement("div");
    toolbar.className = "fd-toolbar";
    const helper = buildHelperParagraph(
      "Write like Final Draft — type a slug to open a scene, Enter drops to the next element, @ references a character / location / asset. Click a circle or square to edit it in place.",
    );
    helper.classList.add("fd-toolbar-helper");
    toolbar.appendChild(helper);

    const commentsToggle = buildStudioButton("Comments", {
      onClick: () => this.toggleComments(),
    });
    commentsToggle.classList.add("fd-comments-toggle");
    commentsToggle.dataset.kokoAction = "script-comments-toggle";
    toolbar.appendChild(commentsToggle);
    box.fill.appendChild(toolbar);

    // Layout: the page + the comments margin.
    const layout = document.createElement("div");
    layout.className = "fd-layout";

    const page = document.createElement("div");
    page.className = "fd-page";
    page.dataset.kokoSurface = "script-page";
    layout.appendChild(page);
    this.pageEl = page;

    const margin = document.createElement("div");
    margin.className = "fd-margin";
    margin.dataset.kokoSurface = "script-comments";
    layout.appendChild(margin);
    this.marginEl = margin;

    box.fill.appendChild(layout);

    // Floating @-autocomplete menu (positioned on demand).
    const menu = document.createElement("div");
    menu.className = "fd-ref-menu";
    menu.hidden = true;
    menu.dataset.kokoSurface = "ref-menu";
    root.appendChild(menu);
    this.refMenuEl = menu;

    root.appendChild(box.outer);
    return root;
  }

  protected onMounted(): void {
    for (const key of ["scenes", "characters", "locations", "entityRefs"] as const) {
      this.unsubscribers.push(this.store.subscribe(key, () => this.render()));
    }
    this.unsubscribers.push(this.commentStore.subscribe(() => this.renderComments()));
    this.unsubscribers.push(this.lineSoundStore.subscribe(() => this.render()));

    const onKokoComment = (event: Event) => this.handleKokoComment(event as CustomEvent);
    window.addEventListener("koko:script-comment", onKokoComment);
    this.unsubscribers.push(() =>
      window.removeEventListener("koko:script-comment", onKokoComment),
    );

    const onDocClick = (event: MouseEvent) => {
      if (!this.refMenu) return;
      if (this.refMenuEl && this.refMenuEl.contains(event.target as Node)) return;
      this.hideRefMenu();
    };
    document.addEventListener("mousedown", onDocClick, true);
    this.unsubscribers.push(() =>
      document.removeEventListener("mousedown", onDocClick, true),
    );

    this.applyCommentsClass();
    this.render();
  }

  protected onShown(): void {
    this.render();
  }

  protected onHidden(): void {
    this.voicePlayer.stop();
    this.hideRefMenu();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.voicePlayer.stop();
  }

  // ------------------------------------------------------------------
  // Entity index (for @-autocomplete + ref resolution)
  // ------------------------------------------------------------------

  private buildEntityIndex(): {
    list: EntityIndexEntry[];
    byName: Map<string, EntityIndexEntry>;
    byId: Map<string, EntityIndexEntry>;
  } {
    const list: EntityIndexEntry[] = [];
    for (const c of this.store.get("characters")) {
      list.push({ type: "character", id: c.id, name: c.name });
    }
    for (const l of this.store.get("locations")) {
      list.push({ type: "location", id: l.id, name: l.name });
    }
    for (const a of this.store.get("assets")) {
      list.push({ type: "asset", id: a.id, name: a.name });
    }
    const looksByChar = this.store.get("costumeLooksByCharacterId");
    for (const looks of Object.values(looksByChar)) {
      for (const look of looks) {
        if (look.note?.trim()) {
          list.push({ type: "look", id: look.id, name: look.note.trim() });
        }
      }
    }
    const byName = new Map<string, EntityIndexEntry>();
    const byId = new Map<string, EntityIndexEntry>();
    for (const entry of list) {
      const key = entry.name.trim().toLowerCase();
      if (key && !byName.has(key)) byName.set(key, entry);
      byId.set(entry.id, entry);
    }
    return { list, byName, byId };
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  private render(): void {
    if (!this.pageEl) return;
    this.pageEl.replaceChildren();
    const scenes = this.store.get("scenes");

    if (scenes.length === 0) {
      this.pageEl.appendChild(this.buildEmptyState());
      this.renderComments();
      this.applyPendingFocus();
      return;
    }

    let sceneNumber = 0;
    for (const scene of scenes) {
      sceneNumber += 1;
      this.pageEl.appendChild(this.buildSceneBlock(scene, sceneNumber));
    }

    // Subtle end-of-document add-scene hook (free-type is primary; this
    // stays for the Koko bridge choreography + timeline live-update).
    const footer = document.createElement("div");
    footer.className = "fd-doc-footer";
    const addScene = buildStudioButton("+ Add scene", {
      onClick: () => this.startNewScene(),
    });
    addScene.classList.add("fd-add-scene");
    addScene.dataset.kokoAction = "scene-add";
    footer.appendChild(addScene);
    this.pageEl.appendChild(footer);

    this.renderComments();
    this.applyPendingFocus();
  }

  private buildEmptyState(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fd-empty";

    const drop = document.createElement("div");
    drop.className = "fd-drop";
    drop.dataset.kokoAction = "script-inline-drop";
    const dropIcon = document.createElement("div");
    dropIcon.className = "fd-drop-icon";
    dropIcon.textContent = "▣";
    const dropLabel = document.createElement("div");
    dropLabel.className = "fd-drop-label";
    dropLabel.textContent = "Drop a screenplay PDF to import";
    const dropHint = document.createElement("div");
    dropHint.className = "fd-drop-hint";
    dropHint.textContent =
      "Koko reads it into Final Draft and fills your cast, locations, and props.";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "application/pdf,.pdf";
    // NB: a dedicated class (not the shared `studio-hidden-file-input`) so
    // it doesn't collide with the Upload panel's input in tests/queries.
    fileInput.className = "fd-file-input";
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) void this.handleInlineUpload(file);
    });
    const chooseButton = buildStudioButton("Choose PDF", {
      variant: "primary",
      onClick: () => fileInput.click(),
    });
    chooseButton.dataset.kokoAction = "script-inline-choose";

    drop.append(dropIcon, dropLabel, dropHint, chooseButton, fileInput);
    drop.addEventListener("dragover", (e) => {
      e.preventDefault();
      drop.classList.add("is-dragover");
    });
    drop.addEventListener("dragleave", () => drop.classList.remove("is-dragover"));
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      drop.classList.remove("is-dragover");
      const file = e.dataTransfer?.files?.[0];
      if (file) void this.handleInlineUpload(file);
    });
    wrap.appendChild(drop);

    const status = document.createElement("div");
    status.className = "fd-inline-status";
    status.hidden = true;
    wrap.appendChild(status);

    const orRow = document.createElement("div");
    orRow.className = "fd-or";
    orRow.textContent = "— or just start writing —";
    wrap.appendChild(orRow);

    // A single blank, editable line that becomes the first scene.
    const startLine = document.createElement("div");
    startLine.className = "fd-line fd-action fd-pending";
    const text = document.createElement("div");
    text.className = "fd-text";
    text.contentEditable = "true";
    text.spellcheck = false;
    text.dataset.placeholder = "INT. LOCATION - DAY  (or any action line)";
    text.dataset.kokoAction = "script-pending-line";
    // Guard so Enter (then the resulting blur) doesn't create two scenes.
    let consumed = false;
    const promote = (value: string) => {
      if (consumed) return;
      consumed = true;
      this.startNewScene(value);
    };
    text.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        promote((text.textContent ?? "").trim());
      }
    });
    text.addEventListener("blur", () => {
      const value = (text.textContent ?? "").trim();
      if (value) promote(value);
    });
    startLine.appendChild(text);
    wrap.appendChild(startLine);
    return wrap;
  }

  private buildSceneBlock(scene: StudioScene, sceneNumber: number): HTMLElement {
    const block = document.createElement("section");
    block.className = "fd-scene";
    block.dataset.sceneId = scene.id;

    const characters = this.store.get("characters");
    const elements = resolveSceneElements(scene, characters);
    if (elements.length === 0) {
      elements.push({ id: makeElementId(), type: "action", text: "", characterId: null });
    }

    // Where to slot the inline storyboard strip — before the first cue.
    let stripIndex = elements.findIndex(
      (el) =>
        el.type === "character" || el.type === "dialogue" || el.type === "parenthetical",
    );
    if (stripIndex === -1) stripIndex = elements.length;

    elements.forEach((_el, index) => {
      if (index === stripIndex) {
        block.appendChild(this.buildStoryboardStrip(scene, sceneNumber));
      }
      block.appendChild(this.buildLineRow(scene, elements, index, sceneNumber));
    });
    if (stripIndex >= elements.length) {
      block.appendChild(this.buildStoryboardStrip(scene, sceneNumber));
    }

    // Hover controls — delete scene.
    const controls = document.createElement("div");
    controls.className = "fd-scene-controls";
    const del = buildStudioButton("Delete scene", {
      variant: "danger",
      onClick: () => this.store.deleteScene(scene.id),
    });
    del.dataset.kokoAction = "scene-delete";
    del.dataset.id = scene.id;
    controls.appendChild(del);
    block.appendChild(controls);

    return block;
  }

  private buildLineRow(
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
    sceneNumber: number,
  ): HTMLElement {
    const el = elements[index];
    const row = document.createElement("div");
    row.className = `fd-line fd-${el.type}`;
    row.dataset.sceneId = scene.id;
    row.dataset.lineIndex = String(index);
    row.dataset.elementId = el.id;

    // Per-line type chip at the line start.
    row.appendChild(this.buildTypeChip(scene, elements, index));

    if (el.type === "slug") {
      const sceneNo = document.createElement("span");
      sceneNo.className = "fd-scene-no";
      sceneNo.textContent = String(sceneNumber);
      row.appendChild(sceneNo);
      row.appendChild(this.buildLocationThumb(scene));
    }

    const character =
      el.type === "character" && el.characterId
        ? this.store.get("characters").find((c) => c.id === el.characterId) ?? null
        : null;
    if (el.type === "character" && character) {
      row.appendChild(this.buildCharacterCircle(character));
    }

    const text = document.createElement("div");
    text.className = "fd-text";
    text.contentEditable = "true";
    text.spellcheck = false;
    text.dataset.kokoAction = "script-line";
    text.dataset.sceneId = scene.id;
    text.dataset.lineIndex = String(index);
    text.dataset.elementId = el.id;
    text.dataset.placeholder = placeholderFor(el.type);
    this.renderTextDisplay(text, el);

    text.addEventListener("focus", () => this.onTextFocus(text, el));
    text.addEventListener("input", () => this.onTextInput(text, scene, index));
    text.addEventListener("blur", () => this.onTextBlur(text, scene, index));
    text.addEventListener("keydown", (event) =>
      this.onTextKeydown(event, text, scene, elements, index),
    );
    row.appendChild(text);

    // Dialogue: play (character voice) + sound edit.
    if (el.type === "dialogue") {
      const speaker = this.resolveSpeaker(elements, index);
      row.appendChild(this.buildPlayButton(el, speaker));
      row.appendChild(this.buildSoundButton(el, speaker));
    }

    // Comment affordance (margin anchor button).
    row.appendChild(this.buildCommentButton(scene, el));

    return row;
  }

  private buildTypeChip(
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
  ): HTMLElement {
    const el = elements[index];
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "fd-line-type";
    chip.textContent = typeLabel(el.type);
    chip.title = `Line type: ${typeLabel(el.type)} — click to change`;
    chip.dataset.kokoAction = "script-line-type";
    chip.dataset.elementId = el.id;
    chip.dataset.type = el.type;
    chip.addEventListener("mousedown", (e) => e.preventDefault());
    chip.addEventListener("click", () => {
      const next = cycleType(el.type);
      this.pinnedTypes.add(el.id);
      this.setElementType(scene, index, next);
    });
    return chip;
  }

  private buildLocationThumb(scene: StudioScene): HTMLElement {
    const thumb = document.createElement("button");
    thumb.type = "button";
    thumb.className = "fd-loc-thumb";
    thumb.dataset.kokoAction = "script-open-location";
    const location = scene.locationId
      ? this.store.get("locations").find((l) => l.id === scene.locationId) ?? null
      : null;
    if (location) {
      thumb.dataset.id = location.id;
      const image = location.panoramaImage || location.referenceImage || null;
      if (image) {
        thumb.classList.add("is-filled");
        thumb.style.backgroundImage = `url("${image}")`;
      } else {
        thumb.classList.add("is-filled", "is-placeholder");
        thumb.style.setProperty("--loc-hue", String(hashHue(location.id)));
      }
      thumb.title = `${location.name} — click to edit the location`;
      thumb.addEventListener("click", () => this.openEntityDrawerFor("location", location.id));
    } else {
      thumb.textContent = "img";
      thumb.title = "No location set for this scene";
    }
    return thumb;
  }

  private buildCharacterCircle(character: StudioCharacter): HTMLElement {
    const wrap = document.createElement("button");
    wrap.type = "button";
    wrap.className = "fd-char-circle";
    wrap.title = `Edit ${character.name}`;
    wrap.dataset.kokoAction = "open-character";
    wrap.dataset.id = character.id;
    const image = character.referenceImage;
    if (image) {
      const img = document.createElement("img");
      img.src = image;
      img.alt = character.name;
      img.draggable = false;
      wrap.appendChild(img);
    } else {
      wrap.appendChild(
        buildPixelIdenticonImage(character.id, { alt: character.name, pixelSize: 56 }),
      );
    }
    const badge = document.createElement("span");
    badge.className = "fd-edit-badge";
    badge.textContent = "✎";
    wrap.appendChild(badge);
    wrap.addEventListener("click", () => this.openEntityDrawerFor("character", character.id));
    return wrap;
  }

  private buildPlayButton(el: ScriptElement, speaker: StudioCharacter | null): HTMLElement {
    const play = document.createElement("button");
    play.type = "button";
    play.className = "fd-play";
    play.textContent = "▶";
    play.title = speaker ? `Play in ${speaker.name}'s voice` : "Play this line";
    play.dataset.kokoAction = "script-line-play";
    play.dataset.elementId = el.id;
    play.addEventListener("click", () => this.playLine(play, el, speaker));
    return play;
  }

  private buildSoundButton(el: ScriptElement, speaker: StudioCharacter | null): HTMLElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fd-sound";
    button.textContent = "♪";
    const sound = this.lineSoundStore.get(el.id);
    if (sound && (sound.delivery !== "neutral" || sound.tone || (sound.speed ?? 1) !== 1)) {
      button.classList.add("is-set");
    }
    button.title = "Edit this line's sound (delivery, tone, speed)";
    button.dataset.kokoAction = "script-line-sound";
    button.dataset.elementId = el.id;
    button.addEventListener("click", () => this.openSoundEditor(el, speaker));
    return button;
  }

  private buildCommentButton(scene: StudioScene, el: ScriptElement): HTMLElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fd-comment-btn";
    const threads = this.commentStore.listForElement(el.id);
    const open = threads.filter((c) => !c.resolved).length;
    if (open > 0) {
      button.classList.add("has-comments");
      button.textContent = String(open);
      button.title = `${open} comment${open === 1 ? "" : "s"} — click to view`;
    } else {
      button.textContent = "💬";
      button.title = "Comment on this line";
    }
    button.dataset.kokoAction = "script-line-comment";
    button.dataset.elementId = el.id;
    button.addEventListener("click", () => this.openCommentComposer(scene, el));
    return button;
  }

  private buildStoryboardStrip(scene: StudioScene, sceneNumber: number): HTMLElement {
    const strip = document.createElement("div");
    strip.className = "fd-storyboard-strip";
    const frames = this.store.get("storyboardFramesBySceneId")[scene.id] ?? [];
    const ready = frames.filter((f) => f.image);
    if (ready.length > 0) {
      ready.slice(0, 3).forEach((frame, i) => {
        const el = document.createElement("div");
        el.className = "fd-sb-frame is-ready";
        el.style.backgroundImage = `url("${frame.image}")`;
        const cap = document.createElement("span");
        cap.className = "fd-sb-cap";
        cap.textContent = frame.caption || `shot ${i + 1}`;
        el.appendChild(cap);
        strip.appendChild(el);
      });
    } else if (scene.storyboardReady || scene.videoReady) {
      const frame = document.createElement("div");
      frame.className = "fd-sb-frame is-ready is-placeholder";
      frame.style.setProperty("--sb-hue", String(hashHue(scene.id)));
      const cap = document.createElement("span");
      cap.className = "fd-sb-cap";
      cap.textContent = `shot ${sceneNumber}`;
      frame.appendChild(cap);
      strip.appendChild(frame);
    } else {
      const empty = document.createElement("div");
      empty.className = "fd-sb-frame is-empty";
      empty.textContent = "no storyboard yet";
      strip.appendChild(empty);
    }
    return strip;
  }

  // ------------------------------------------------------------------
  // Text rendering — @-ref pills (display when not focused)
  // ------------------------------------------------------------------

  private renderTextDisplay(textEl: HTMLElement, el: ScriptElement): void {
    if (this.focusedElementId === el.id) {
      textEl.textContent = el.text;
      return;
    }
    const { byName } = this.buildEntityIndex();
    const names = Array.from(byName.values()).map((e) => e.name);
    const tokens = extractRefTokens(el.text, names);
    if (tokens.length === 0) {
      textEl.textContent = el.text;
      return;
    }
    textEl.replaceChildren();
    let cursor = 0;
    for (const token of tokens) {
      if (token.start > cursor) {
        textEl.appendChild(document.createTextNode(el.text.slice(cursor, token.start)));
      }
      const entry = byName.get(token.entityName.trim().toLowerCase()) ?? null;
      const pill = document.createElement("span");
      pill.className = "fd-ref-pill";
      pill.contentEditable = "false";
      pill.textContent = token.raw;
      if (entry) {
        pill.dataset.refType = entry.type;
        pill.dataset.entityId = entry.id;
        pill.classList.add(`is-${entry.type}`);
        pill.title = `${entry.type}: ${entry.name} — click to edit`;
        pill.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        pill.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (entry.type === "look") return;
          this.openEntityDrawerFor(entry.type, entry.id);
        });
      } else {
        pill.classList.add("is-unknown");
      }
      textEl.appendChild(pill);
      cursor = token.end;
    }
    if (cursor < el.text.length) {
      textEl.appendChild(document.createTextNode(el.text.slice(cursor)));
    }
  }

  // ------------------------------------------------------------------
  // Text events
  // ------------------------------------------------------------------

  private onTextFocus(textEl: HTMLElement, el: ScriptElement): void {
    this.focusedElementId = el.id;
    if (textEl.textContent !== el.text || textEl.childElementCount > 0) {
      const caret = el.text.length;
      textEl.textContent = el.text;
      setCaretOffset(textEl, caret);
    }
  }

  private onTextInput(textEl: HTMLElement, scene: StudioScene, index: number): void {
    this.updateRefMenu(textEl, scene, index);
  }

  private onTextBlur(textEl: HTMLElement, scene: StudioScene, index: number): void {
    // Defer so a click on the ref-menu can fire first.
    window.setTimeout(() => {
      if (this.refMenu && this.refMenu.textEl === textEl) return;
      if (this.focusedElementId) this.focusedElementId = null;
      this.commitElement(scene, index, textEl.textContent ?? "");
    }, 0);
  }

  private onTextKeydown(
    event: KeyboardEvent,
    textEl: HTMLElement,
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
  ): void {
    // Ref-menu navigation takes precedence.
    if (this.refMenu && this.refMenu.textEl === textEl) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.moveRefSelection(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.moveRefSelection(-1);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        this.applyRefSelection();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.hideRefMenu();
        return;
      }
    }

    if (event.key === "Enter") {
      event.preventDefault();
      this.focusedElementId = null;
      this.commitElement(scene, index, textEl.textContent ?? "");
      this.addLineAfter(scene, index);
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      const el = elements[index];
      const next = cycleType(el.type);
      this.pinnedTypes.add(el.id);
      this.focusedElementId = null;
      this.commitElement(scene, index, textEl.textContent ?? "", next);
      this.pendingFocus = { sceneId: scene.id, index };
      return;
    }
    if (event.key === "Backspace") {
      const caret = getCaretOffset(textEl);
      if (caret === 0 && (textEl.textContent ?? "").length === 0 && index > 0) {
        event.preventDefault();
        this.removeLine(scene, index);
      }
    }
  }

  // ------------------------------------------------------------------
  // @-autocomplete
  // ------------------------------------------------------------------

  private updateRefMenu(textEl: HTMLElement, scene: StudioScene, index: number): void {
    const text = textEl.textContent ?? "";
    const caret = getCaretOffset(textEl);
    let at = -1;
    for (let i = caret - 1; i >= 0; i -= 1) {
      const ch = text[i];
      if (ch === "@") {
        const before = text[i - 1];
        if (before === undefined || /\s/.test(before)) at = i;
        break;
      }
      if (/\s/.test(ch)) break;
    }
    if (at === -1) {
      this.hideRefMenu();
      return;
    }
    const query = text.slice(at + 1, caret);
    if (/[\s@]/.test(query)) {
      this.hideRefMenu();
      return;
    }
    const items = this.buildRefMenuItems(query);
    this.refMenu = { textEl, scene, index, tokenStart: at, query, items, selected: 0 };
    this.renderRefMenu(textEl);
  }

  private buildRefMenuItems(query: string): RefMenuItem[] {
    const { list } = this.buildEntityIndex();
    const q = query.trim().toLowerCase();
    const matches = list
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map<RefMenuItem>((entry) => ({
        label: entry.name,
        hint: entry.type,
        entry,
        name: entry.name,
      }));
    const items = [...matches];
    if (q) {
      const exists = list.some((e) => e.name.toLowerCase() === q);
      if (!exists) {
        const display = query.trim();
        items.push(
          { label: `Create character “${display}”`, hint: "new", entry: null, createKind: "character", name: display },
          { label: `Create location “${display}”`, hint: "new", entry: null, createKind: "location", name: display },
          { label: `Create asset “${display}”`, hint: "new", entry: null, createKind: "asset", name: display },
        );
      }
    }
    return items;
  }

  private renderRefMenu(textEl: HTMLElement): void {
    const menu = this.refMenuEl;
    if (!menu || !this.refMenu) return;
    if (this.refMenu.items.length === 0) {
      this.hideRefMenu();
      return;
    }
    menu.replaceChildren();
    this.refMenu.items.forEach((item, i) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "fd-ref-item";
      if (i === this.refMenu!.selected) row.classList.add("is-selected");
      row.dataset.kokoAction = "ref-menu-item";
      const label = document.createElement("span");
      label.className = "fd-ref-item-label";
      label.textContent = item.label;
      const hint = document.createElement("span");
      hint.className = "fd-ref-item-hint";
      hint.textContent = item.hint;
      row.append(label, hint);
      row.addEventListener("mousedown", (e) => e.preventDefault());
      row.addEventListener("click", () => {
        this.refMenu!.selected = i;
        this.applyRefSelection();
      });
      menu.appendChild(row);
    });
    const rect = textEl.getBoundingClientRect();
    menu.style.left = `${Math.round(rect.left)}px`;
    menu.style.top = `${Math.round(rect.bottom + 4)}px`;
    menu.hidden = false;
  }

  private moveRefSelection(delta: number): void {
    if (!this.refMenu) return;
    const n = this.refMenu.items.length;
    this.refMenu.selected = (this.refMenu.selected + delta + n) % n;
    this.renderRefMenu(this.refMenu.textEl);
  }

  private applyRefSelection(): void {
    if (!this.refMenu) return;
    const { textEl, scene, index, tokenStart, items, selected } = this.refMenu;
    const item = items[selected];
    if (!item) {
      this.hideRefMenu();
      return;
    }
    let name = item.name;
    if (!item.entry && item.createKind) {
      const created = this.autoCreateEntity(item.createKind, name);
      name = created.name;
    } else if (item.entry) {
      name = item.entry.name;
    }
    const text = textEl.textContent ?? "";
    const caret = getCaretOffset(textEl);
    const before = text.slice(0, tokenStart);
    const after = text.slice(caret);
    const insert = `@${name}`;
    const sep = after.startsWith(" ") || after === "" ? "" : " ";
    const next = `${before}${insert}${sep}${after}`;
    textEl.textContent = next;
    setCaretOffset(textEl, before.length + insert.length + 1);
    this.hideRefMenu();
    this.commitElement(scene, index, next, undefined, { keepFocus: true });
  }

  private hideRefMenu(): void {
    this.refMenu = null;
    if (this.refMenuEl) this.refMenuEl.hidden = true;
  }

  // ------------------------------------------------------------------
  // Commit / living-document mutations
  // ------------------------------------------------------------------

  private getWorkingElements(scene: StudioScene): ScriptElement[] {
    const current = this.store.get("scenes").find((s) => s.id === scene.id);
    if (!current) return [];
    const els = resolveSceneElements(current, this.store.get("characters"));
    if (els.length === 0) {
      els.push({ id: makeElementId(), type: "action", text: "", characterId: null });
    }
    return els;
  }

  private commitElement(
    scene: StudioScene,
    index: number,
    rawText: string,
    forcedType?: ElementType,
    options: { keepFocus?: boolean } = {},
  ): void {
    const els = this.getWorkingElements(scene);
    if (index < 0 || index >= els.length) return;
    const el = els[index];
    const prevType = index > 0 ? els[index - 1].type : null;
    const characters = this.store.get("characters");

    let type: ElementType;
    if (forcedType) {
      type = forcedType;
    } else {
      const known = !!findCharacterByName(rawText.trim(), characters);
      const pinned = this.pinnedTypes.has(el.id) ? el.type : null;
      type = classifyType(rawText, prevType, known, pinned);
    }

    const textChanged = rawText !== el.text;
    const typeChanged = type !== el.type;

    // Living-doc: a character cue creates the character; a slug creates
    // the location and links it to the scene.
    let characterId: string | null = null;
    let locationPatch: { locationId?: string | null } = {};
    if (type === "character") {
      const name = rawText.trim();
      if (name) characterId = this.store.autoCreateCharacter(name).id;
    }
    if (type === "slug") {
      const parsed = parseSlug(rawText);
      if (parsed) {
        const location = this.store.autoCreateLocation(parsed.locationName, {
          intExt: parsed.intExt ?? undefined,
          timeOfDay: parsed.timeOfDay ? [parsed.timeOfDay] : undefined,
        });
        locationPatch = { locationId: location.id };
      }
    }

    els[index] = { ...el, text: rawText, type, characterId };

    if (textChanged || typeChanged || Object.keys(locationPatch).length > 0) {
      this.store.updateScene(scene.id, elementsToBody(els), {
        elements: els,
        ...locationPatch,
        ...(textChanged
          ? { storyboardReady: false, videoReady: false, generatedVoiceLine: false }
          : {}),
      });
    }

    this.syncSceneRefs(scene.id, els);

    if (options.keepFocus) {
      this.pendingFocus = { sceneId: scene.id, index };
    }
  }

  private syncSceneRefs(sceneId: string, els: ScriptElement[]): void {
    const { byName } = this.buildEntityIndex();
    const names = Array.from(byName.values()).map((e) => e.name);
    const refs: Array<{
      type: "character" | "location" | "look" | "asset";
      entityId: string;
      elementId: string;
      column: number;
    }> = [];
    for (const el of els) {
      const tokens = extractRefTokens(el.text, names);
      for (const token of tokens) {
        const key = token.entityName.trim().toLowerCase();
        let entry = byName.get(key);
        if (!entry) {
          const created = this.store.autoCreateAsset(token.entityName.trim());
          entry = { type: "asset", id: created.id, name: created.name };
          byName.set(key, entry);
          names.push(created.name);
        }
        refs.push({
          type: entry.type,
          entityId: entry.id,
          elementId: el.id,
          column: token.start,
        });
      }
    }
    this.store.replaceSceneRefs(sceneId, refs);
  }

  private setElementType(scene: StudioScene, index: number, type: ElementType): void {
    const els = this.getWorkingElements(scene);
    if (index < 0 || index >= els.length) return;
    this.commitElement(scene, index, els[index].text, type);
    this.pendingFocus = { sceneId: scene.id, index };
  }

  private addLineAfter(scene: StudioScene, index: number): void {
    const els = this.getWorkingElements(scene);
    const at = Math.min(index + 1, els.length);
    const prevType = els[index]?.type ?? "action";
    const newEl: ScriptElement = {
      id: makeElementId(),
      type: nextTypeOnEnter(prevType),
      text: "",
      characterId: null,
    };
    els.splice(at, 0, newEl);
    this.store.updateScene(scene.id, elementsToBody(els), { elements: els });
    this.pendingFocus = { sceneId: scene.id, index: at };
  }

  private removeLine(scene: StudioScene, index: number): void {
    const els = this.getWorkingElements(scene);
    if (els.length <= 1 || index <= 0) return;
    els.splice(index, 1);
    this.store.updateScene(scene.id, elementsToBody(els), { elements: els });
    this.pendingFocus = { sceneId: scene.id, index: Math.max(0, index - 1) };
  }

  private startNewScene(initial = ""): void {
    const text = initial.trim();
    const scene = this.store.addScene(text);
    const els = bodyToElements(text || "", this.store.get("characters"));
    const first = els[0];
    if (first) {
      let locationId: string | null = null;
      if (first.type === "character" && first.text.trim()) {
        first.characterId = this.store.autoCreateCharacter(first.text.trim()).id;
      }
      if (first.type === "slug") {
        const parsed = parseSlug(first.text);
        if (parsed) {
          locationId = this.store.autoCreateLocation(parsed.locationName, {
            intExt: parsed.intExt ?? undefined,
          }).id;
        }
      }
      this.store.updateScene(scene.id, elementsToBody(els), {
        elements: els,
        ...(locationId ? { locationId } : {}),
      });
    }
    this.pendingFocus = { sceneId: scene.id, index: Math.max(0, els.length - 1) };
  }

  // ------------------------------------------------------------------
  // Focus management
  // ------------------------------------------------------------------

  private applyPendingFocus(): void {
    const target = this.pendingFocus;
    this.pendingFocus = null;
    if (!target || !this.pageEl) return;
    requestAnimationFrame(() => {
      const el = this.pageEl?.querySelector<HTMLElement>(
        `.fd-text[data-scene-id="${cssEscape(target.sceneId)}"][data-line-index="${target.index}"]`,
      );
      if (el) {
        this.focusedElementId = el.dataset.elementId ?? null;
        el.focus();
        setCaretOffset(el, (el.textContent ?? "").length);
      }
    });
  }

  // ------------------------------------------------------------------
  // Per-line voice + sound (G8)
  // ------------------------------------------------------------------

  private resolveSpeaker(elements: ScriptElement[], index: number): StudioCharacter | null {
    for (let i = index; i >= 0; i -= 1) {
      if (elements[i].type === "character" && elements[i].characterId) {
        return (
          this.store.get("characters").find((c) => c.id === elements[i].characterId) ?? null
        );
      }
      if (elements[i].type === "slug") break;
    }
    return null;
  }

  private async playLine(
    button: HTMLButtonElement,
    el: ScriptElement,
    speaker: StudioCharacter | null,
  ): Promise<void> {
    const text = el.text.trim();
    if (!text) return;
    const sound = this.lineSoundStore.get(el.id);
    const voice = resolveVoice(speaker, sound);
    button.classList.add("is-playing");
    await this.voicePlayer
      .speak(text, voice, { onEnd: () => button.classList.remove("is-playing") })
      .catch(() => button.classList.remove("is-playing"));
  }

  private openSoundEditor(el: ScriptElement, speaker: StudioCharacter | null): void {
    const current = this.lineSoundStore.get(el.id) ?? { ...DEFAULT_LINE_SOUND };
    openEntityDrawer({
      title: "Line sound",
      subtitle: speaker ? `${speaker.name}'s voice` : "Dialogue delivery",
      render: (body) => {
        const note = document.createElement("p");
        note.className = "studio-helper";
        note.textContent = speaker
          ? `Tune how this line is performed in ${speaker.name}'s voice. ▶ uses these settings.`
          : "Tune how this line is performed. ▶ uses these settings.";
        body.appendChild(note);

        const draft: LineSound = { ...current };

        const deliveryRow = document.createElement("div");
        deliveryRow.className = "fd-sound-deliveries";
        const deliveries: Delivery[] = ["neutral", "whisper", "warm", "shout"];
        const buttons: HTMLButtonElement[] = [];
        for (const d of deliveries) {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "fd-sound-delivery";
          b.textContent = d;
          b.dataset.delivery = d;
          if (draft.delivery === d) b.classList.add("is-active");
          b.addEventListener("click", () => {
            draft.delivery = d;
            buttons.forEach((x) => x.classList.toggle("is-active", x === b));
          });
          buttons.push(b);
          deliveryRow.appendChild(b);
        }
        body.appendChild(labeled("Delivery", deliveryRow));

        const tone = document.createElement("input");
        tone.type = "text";
        tone.className = "studio-input fd-sound-tone";
        tone.placeholder = "e.g. bitter, playful, exhausted";
        tone.value = draft.tone ?? "";
        tone.addEventListener("input", () => (draft.tone = tone.value));
        body.appendChild(labeled("Tone", tone));

        const speed = document.createElement("input");
        speed.type = "range";
        speed.min = "0.7";
        speed.max = "1.4";
        speed.step = "0.05";
        speed.value = String(draft.speed ?? 1);
        speed.className = "fd-sound-speed";
        const speedVal = document.createElement("span");
        speedVal.className = "fd-sound-speed-val";
        speedVal.textContent = `${Number(speed.value).toFixed(2)}×`;
        speed.addEventListener("input", () => {
          draft.speed = Number(speed.value);
          speedVal.textContent = `${Number(speed.value).toFixed(2)}×`;
        });
        const speedWrap = document.createElement("div");
        speedWrap.className = "fd-sound-speed-row";
        speedWrap.append(speed, speedVal);
        body.appendChild(labeled("Speed", speedWrap));

        const actions = document.createElement("div");
        actions.className = "fd-sound-actions";
        const preview = buildStudioButton("▶ Preview", {
          onClick: () => {
            const voice = resolveVoice(speaker, draft);
            void this.voicePlayer.speak(el.text, voice);
          },
        });
        preview.dataset.kokoAction = "script-sound-preview";
        const save = buildStudioButton("Save", {
          variant: "primary",
          onClick: () => {
            this.lineSoundStore.set(el.id, draft);
            closeEntityDrawer();
          },
        });
        save.dataset.kokoAction = "script-sound-save";
        actions.append(preview, save);
        body.appendChild(actions);
      },
    });
  }

  // ------------------------------------------------------------------
  // Comments (G-Docs style margin threads)
  // ------------------------------------------------------------------

  private toggleComments(): void {
    this.showComments = !this.showComments;
    this.applyCommentsClass();
  }

  private applyCommentsClass(): void {
    this.rootEl?.classList.toggle("fd-comments-on", this.showComments);
    const toggle = this.rootEl?.querySelector<HTMLElement>(".fd-comments-toggle");
    toggle?.classList.toggle("is-on", this.showComments);
  }

  private handleKokoComment(event: CustomEvent): void {
    const detail = (event.detail ?? {}) as {
      sceneId?: string;
      elementId?: string;
      text?: string;
    };
    if (!detail.elementId || !detail.text) return;
    const scene = this.store
      .get("scenes")
      .find((s) => (s.elements ?? []).some((el) => el.id === detail.elementId));
    const sceneId = detail.sceneId ?? scene?.id ?? "";
    const quote = scene?.elements?.find((el) => el.id === detail.elementId)?.text ?? "";
    this.commentStore.add({
      sceneId,
      elementId: detail.elementId,
      quote,
      text: detail.text,
      author: "koko",
    });
  }

  private openCommentComposer(scene: StudioScene, el: ScriptElement): void {
    this.showComments = true;
    this.applyCommentsClass();
    this.renderComments();
    requestAnimationFrame(() => {
      const input = this.marginEl?.querySelector<HTMLTextAreaElement>(
        `.fd-comment-thread[data-element-id="${cssEscape(el.id)}"] .fd-comment-input`,
      );
      if (input) {
        input.focus();
        return;
      }
      this.openInlineComposer(scene, el);
    });
  }

  private openInlineComposer(scene: StudioScene, el: ScriptElement): void {
    if (!this.marginEl) return;
    this.marginEl.querySelector(".fd-comment-new")?.remove();
    const composer = document.createElement("div");
    composer.className = "fd-comment-thread fd-comment-new";
    composer.dataset.elementId = el.id;
    const quote = document.createElement("div");
    quote.className = "fd-comment-quote";
    quote.textContent = el.text || "(empty line)";
    composer.appendChild(quote);
    composer.appendChild(this.buildComposerForm(scene, el));
    this.marginEl.prepend(composer);
    composer.querySelector<HTMLTextAreaElement>(".fd-comment-input")?.focus();
  }

  private buildComposerForm(scene: StudioScene, el: ScriptElement): HTMLElement {
    const form = document.createElement("div");
    form.className = "fd-comment-form";
    const input = document.createElement("textarea");
    input.className = "fd-comment-input studio-input";
    input.placeholder = "Add a comment…";
    input.rows = 2;
    input.dataset.kokoAction = "script-comment-input";
    const actions = document.createElement("div");
    actions.className = "fd-comment-form-actions";
    const submit = buildStudioButton("Comment", {
      variant: "primary",
      onClick: () => {
        const text = input.value.trim();
        if (!text) return;
        this.commentStore.add({
          sceneId: scene.id,
          elementId: el.id,
          quote: el.text,
          text,
        });
        input.value = "";
      },
    });
    submit.dataset.kokoAction = "script-comment-submit";
    const cancel = buildStudioButton("Cancel", {
      onClick: () => this.renderComments(),
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        submit.click();
      }
    });
    actions.append(submit, cancel);
    form.append(input, actions);
    return form;
  }

  private renderComments(): void {
    if (!this.marginEl) return;
    this.marginEl.replaceChildren();

    const title = document.createElement("div");
    title.className = "fd-margin-title";
    title.textContent = "Comments";
    this.marginEl.appendChild(title);

    const scenes = this.store.get("scenes");
    const elementById = new Map<string, { scene: StudioScene; el: ScriptElement }>();
    for (const scene of scenes) {
      for (const el of resolveSceneElements(scene, this.store.get("characters"))) {
        elementById.set(el.id, { scene, el });
      }
    }

    const all = [...this.commentStore.list()].sort((a, b) => a.createdAt - b.createdAt);
    const byElement = new Map<string, ScriptComment[]>();
    for (const c of all) {
      const arr = byElement.get(c.elementId) ?? [];
      arr.push(c);
      byElement.set(c.elementId, arr);
    }

    if (byElement.size === 0) {
      const empty = document.createElement("div");
      empty.className = "fd-margin-empty";
      empty.textContent = "Select a line's 💬 to start a thread.";
      this.marginEl.appendChild(empty);
      return;
    }

    for (const [elementId, comments] of byElement) {
      const anchor = elementById.get(elementId);
      this.marginEl.appendChild(this.buildCommentThread(elementId, comments, anchor ?? null));
    }
  }

  private buildCommentThread(
    elementId: string,
    comments: ScriptComment[],
    anchor: { scene: StudioScene; el: ScriptElement } | null,
  ): HTMLElement {
    const thread = document.createElement("div");
    thread.className = "fd-comment-thread";
    thread.dataset.elementId = elementId;
    if (comments.every((c) => c.resolved)) thread.classList.add("is-resolved");

    const quote = document.createElement("div");
    quote.className = "fd-comment-quote";
    quote.textContent = (anchor?.el.text || comments[0].quote || "(line)").slice(0, 80);
    thread.appendChild(quote);

    for (const comment of comments) {
      thread.appendChild(this.buildCommentCard(comment));
    }

    if (anchor) {
      thread.appendChild(this.buildComposerForm(anchor.scene, anchor.el));
    }
    return thread;
  }

  private buildCommentCard(comment: ScriptComment): HTMLElement {
    const card = document.createElement("div");
    card.className = "fd-comment-card";
    card.dataset.commentId = comment.id;
    if (comment.resolved) card.classList.add("is-resolved");

    const head = document.createElement("div");
    head.className = "fd-comment-head";
    const author = document.createElement("span");
    author.className = `fd-comment-author is-${comment.author}`;
    author.textContent = comment.author === "koko" ? "Koko" : "You";
    head.appendChild(author);
    card.appendChild(head);

    const text = document.createElement("div");
    text.className = "fd-comment-text";
    text.textContent = comment.text;
    card.appendChild(text);

    const actions = document.createElement("div");
    actions.className = "fd-comment-actions";
    const resolve = document.createElement("button");
    resolve.type = "button";
    resolve.className = "fd-comment-action";
    resolve.textContent = comment.resolved ? "Reopen" : "Resolve";
    resolve.dataset.kokoAction = comment.resolved
      ? "script-comment-reopen"
      : "script-comment-resolve";
    resolve.dataset.id = comment.id;
    resolve.addEventListener("click", () =>
      this.commentStore.setResolved(comment.id, !comment.resolved),
    );
    const del = document.createElement("button");
    del.type = "button";
    del.className = "fd-comment-action is-danger";
    del.textContent = "Delete";
    del.dataset.kokoAction = "script-comment-delete";
    del.dataset.id = comment.id;
    del.addEventListener("click", () => this.commentStore.remove(comment.id));
    actions.append(resolve, del);
    card.appendChild(actions);
    return card;
  }

  // ------------------------------------------------------------------
  // Entity edit-drawer (G6) + living-doc auto-create
  // ------------------------------------------------------------------

  private autoCreateEntity(kind: EntityKind, name: string): { id: string; name: string } {
    switch (kind) {
      case "character":
        return this.store.autoCreateCharacter(name);
      case "location":
        return this.store.autoCreateLocation(name);
      case "look":
        // No global look store — fall back to an asset so the ref resolves.
        return this.store.autoCreateAsset(name);
      default:
        return this.store.autoCreateAsset(name);
    }
  }

  private openEntityDrawerFor(kind: EntityKind, id: string): void {
    if (kind === "character") {
      const character = this.store.get("characters").find((c) => c.id === id);
      if (character) this.openCharacterDrawer(character);
    } else if (kind === "location") {
      const location = this.store.get("locations").find((l) => l.id === id);
      if (location) this.openLocationDrawer(location);
    } else if (kind === "asset") {
      const asset = this.store.get("assets").find((a) => a.id === id);
      if (asset) this.openAssetDrawer(asset);
    }
  }

  private openCharacterDrawer(character: StudioCharacter): void {
    // Reuse the Characters panel's full tabbed detail form (physical +
    // tagging + voice) in the shared drawer (G6) rather than a stub.
    const form = new CharacterDetailForm(this.store, character.id, {
      chrome: "drawer",
      onDeleted: () => closeEntityDrawer(),
    });
    openEntityDrawer({
      title: `Edit ${character.name}`,
      subtitle: "Character",
      render: (body) => body.appendChild(form.getElement()),
      onClose: () => form.destroy(),
    });
  }

  private openLocationDrawer(location: StudioLocation): void {
    openEntityDrawer({
      title: `Edit ${location.name}`,
      subtitle: "Location",
      render: (body) => {
        const draft = { name: location.name, description: location.description };
        const name = textField("Name", draft.name, (v) => (draft.name = v));
        const desc = textArea(
          "Description (materials, layout, scale)",
          draft.description,
          (v) => (draft.description = v),
        );
        body.append(name.wrap, desc.wrap);
        body.appendChild(
          this.buildImageEditRow(
            location.panoramaImage || location.referenceImage || "",
            (next) => this.store.updateLocation(location.id, { panoramaImage: next }),
          ),
        );
        const save = buildStudioButton("Save", {
          variant: "primary",
          onClick: () => {
            this.store.updateLocation(location.id, {
              name: draft.name.trim() || location.name,
              description: draft.description,
            });
            closeEntityDrawer();
          },
        });
        save.dataset.kokoAction = "drawer-save";
        body.appendChild(rowOf(save));
      },
    });
  }

  private openAssetDrawer(asset: StudioAsset): void {
    // Reuse the Assets panel's real edit form (name + physical description
    // with live attribute highlights) in the shared drawer (G6).
    openEntityDrawer({
      title: `Edit ${asset.name}`,
      subtitle: "Asset",
      render: (body) =>
        body.appendChild(
          buildAssetEditForm(this.store, asset, {
            onSaved: () => closeEntityDrawer(),
            onCancel: () => closeEntityDrawer(),
          }),
        ),
    });
  }

  private buildImageEditRow(image: string, onChange: (next: string) => void): HTMLElement {
    const row = document.createElement("div");
    row.className = "fd-drawer-image-row";
    const button = buildStudioButton(image ? "Edit image" : "Add image", {
      onClick: () => openImageEditor({ image: image || "", onChange: (next) => onChange(next) }),
    });
    button.dataset.kokoAction = "drawer-edit-image";
    row.appendChild(button);
    return row;
  }

  // ------------------------------------------------------------------
  // Inline PDF upload (empty-state)
  // ------------------------------------------------------------------

  private async handleInlineUpload(file: File): Promise<void> {
    const status = this.pageEl?.querySelector<HTMLElement>(".fd-inline-status");
    const setStatus = (cls: string, msg: string) => {
      if (!status) return;
      status.hidden = false;
      status.className = `fd-inline-status ${cls}`;
      status.textContent = msg;
    };
    if (!looksLikePdf(file)) {
      setStatus("is-error", `“${file.name}” isn't a PDF. Export your script as PDF.`);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setStatus(
        "is-error",
        `“${file.name}” is ${formatBytes(file.size)} — over the ${formatBytes(MAX_FILE_BYTES)} limit.`,
      );
      return;
    }
    try {
      setStatus("is-working", `Reading “${file.name}”…`);
      const raw = await extractPdfText(file);
      if (raw.trim().length === 0) {
        setStatus("is-error", "No text found — this looks like a scanned PDF.");
        return;
      }
      setStatus("is-working", "Koko is pulling out scenes, cast, and locations…");
      const { text } = clampText(raw);
      const parsed = await analyzeScript(text, resolveWorkerBaseUrl());
      if (countParsedScenes(parsed) === 0) {
        setStatus("is-error", "Couldn't find any scenes in that PDF.");
        return;
      }
      ingestParsedIntoStore(this.store, file.name, parsed);
      // Store change re-renders the page into the populated script.
    } catch (error) {
      console.error("[studio] inline script upload failed:", error);
      setStatus("is-error", toUserMessage(error));
    }
  }
}

// --------------------------------------------------------------------
// Small DOM + caret helpers
// --------------------------------------------------------------------

function labeled(labelText: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "fd-field";
  const label = document.createElement("label");
  label.className = "fd-field-label";
  label.textContent = labelText;
  wrap.append(label, control);
  return wrap;
}

function textField(
  labelText: string,
  value: string,
  onInput: (v: string) => void,
): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = document.createElement("input");
  input.type = "text";
  input.className = "studio-input";
  input.value = value;
  input.addEventListener("input", () => onInput(input.value));
  return { wrap: labeled(labelText, input), input };
}

function textArea(
  labelText: string,
  value: string,
  onInput: (v: string) => void,
): { wrap: HTMLElement; input: HTMLTextAreaElement } {
  const input = document.createElement("textarea");
  input.className = "studio-input";
  input.rows = 4;
  input.value = value;
  input.addEventListener("input", () => onInput(input.value));
  return { wrap: labeled(labelText, input), input };
}

function rowOf(...els: HTMLElement[]): HTMLElement {
  const row = document.createElement("div");
  row.className = "fd-drawer-actions";
  row.append(...els);
  return row;
}

function getCaretOffset(el: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return (el.textContent ?? "").length;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.endContainer)) return (el.textContent ?? "").length;
  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}

function setCaretOffset(el: HTMLElement, offset: number): void {
  const total = (el.textContent ?? "").length;
  const pos = Math.max(0, Math.min(offset, total));
  const range = document.createRange();
  const node = el.firstChild;
  if (node && node.nodeType === Node.TEXT_NODE) {
    range.setStart(node, Math.min(pos, node.textContent?.length ?? 0));
  } else {
    range.selectNodeContents(el);
    range.collapse(false);
  }
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function hashHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
